import {PageModel, SurveyModel} from 'survey-core';
import {CategoryType} from 'types';

export type CategoryItem = CategoryType | undefined | null;
export type CategoryArray = CategoryItem[];
export interface CategorySegment {
  start: number;
  end: number;
  title: string;
  value: string;
}
// Consolidated header sections (RET-2569). The funnel's legacy per-page categories
// were numerous and inconsistently tagged (e.g. the name + sleep-quality pages are
// tagged 'Sleep Profile' yet sit mid-sleep), which fragmented the progress header
// into too many segments. Derive a small, ordered, contiguous set of sections from
// the stable page name instead. Pages with no category stay uncategorized (a gap).
const sectionForPageName = (pageName: string): string => {
  const n = (pageName ?? '').replace(/^page-/, '');
  if (['age', 'gender', 'goal'].includes(n)) {
    return 'About you';
  }
  const isIsiQuestion = n.startsWith('insomnia_') && n !== 'insomnia_care_preference';
  if (
    isIsiQuestion ||
    n === 'name_collection' ||
    n === 'sleep_quality' ||
    n.startsWith('apnea_') ||
    n.startsWith('ess_') ||
    n === 'height' ||
    n === 'weight'
  ) {
    return 'Your sleep';
  }
  // Care preference, insurance/eligibility, and the whole SleepioRx program.
  return 'Your care plan';
};

export const getCategory = (page: PageModel): CategoryType | undefined => {
  //@ts-expect-error I need to access the jsonObj despite it being protected - there is probably a better way to do this.
  const raw: CategoryType | undefined = page.jsonObj?.riseProperties?.category;
  // Preserve "no category" pages as gaps; otherwise collapse to a consolidated section.
  if (!raw) {
    return raw;
  }
  return sectionForPageName(page.name);
};

export const getCategories = (survey: SurveyModel) => {
  return survey.visiblePages.map(p => getCategory(p));
};

export const getCategoryName = (page: PageModel) => {
  const cat = getCategory(page);
  return typeof cat === 'string' ? cat : (cat?.title ?? '');
};

export function getCategoryValue(category: CategoryItem): string {
  if (typeof category === 'string') {
    return category;
  } else if (category && typeof category === 'object') {
    return category.value;
  }
  return '';
}

export function getCategoryTitle(category: CategoryItem): string {
  if (typeof category === 'string') {
    return category;
  } else if (category && typeof category === 'object') {
    return category.title;
  }
  return '';
}

export function makeCategorySegments(arr: CategoryArray): CategorySegment[] {
  const result = [];
  let start = 0;
  let currentValue: string | undefined;

  for (let i = 0; i < arr.length; i++) {
    const normalize = (str: string) => str.trim().replace(/\s+/g, ' ').toUpperCase();

    const currentItem = arr[i];
    const current = currentItem ? normalize(getCategoryValue(currentItem)) : '';

    if (current) {
      if (current !== currentValue) {
        if (currentValue) {
          result.push({
            start,
            end: i - 1,
            title: getCategoryTitle(arr[start]),
            value: currentValue,
          });
        }
        start = i;
        currentValue = current;
      }
    } else {
      let nextDefinedIndex = i + 1;
      while (nextDefinedIndex < arr.length && !getCategoryValue(arr[nextDefinedIndex])) {
        nextDefinedIndex++;
      }

      const nextItem = arr[nextDefinedIndex];
      const nextDefined = nextItem ? normalize(getCategoryValue(nextItem)) : '';

      if (nextDefined !== currentValue) {
        if (currentValue) {
          result.push({
            start,
            end: i - 1,
            title: getCategoryTitle(arr[start]),
            value: currentValue,
          });
        }
        result.push({
          start: i,
          end: nextDefinedIndex - 1,
          title: '',
          value: '',
        });
        start = nextDefinedIndex;
        currentValue = nextDefined;
        i = nextDefinedIndex - 1;
      }
    }
  }

  if (currentValue) {
    result.push({
      start,
      end: arr.length - 1,
      title: getCategoryTitle(arr[start]),
      value: currentValue,
    });
  }
  return result;
}
